import { useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Modal } from "../components/ui/Modal";
import { Input, Select } from "../components/ui/Fields";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { RoomReaction } from "../api/types";

const FORMATS: RoomReaction["format"][] = ["png", "webp", "gif", "svga"];

// Round 7 point 7 — the catalog behind the Android app's room-emoji button
// (roomEmojiButton in VoiceRoomActivity). A seated user's tap plays the
// chosen reaction as an animation over their own seat (realtime broadcast);
// an unseated user's tap posts it as a room_messages "reaction" row instead
// (same realtime-via-poll path gifts/join/invite already use). Nothing
// about which reactions exist, or what they look like, is hardcoded in the
// app — it's all this table, read live.
export function RoomReactionsPage() {
  const { rows, isLoading, error, page, setPage, hasNextPage, reload } = usePagedList<RoomReaction>("/admin/room-reactions");
  const [editing, setEditing] = useState<Partial<RoomReaction> | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  async function save() {
    if (!editing?.name || !editing.asset_url) {
      showToast("Name and asset URL are required", "error");
      return;
    }
    if (!editing.format) {
      showToast("Pick a format", "error");
      return;
    }
    setIsSubmitting(true);
    try {
      const body = {
        name: editing.name,
        asset_url: editing.asset_url,
        format: editing.format,
        sort_order: editing.sort_order ?? 0,
        is_active: editing.is_active ?? true,
      };
      if (editing.id) {
        await apiRequest(`/admin/room-reactions/${editing.id}`, { method: "PATCH", body });
      } else {
        await apiRequest("/admin/room-reactions", { method: "POST", body });
      }
      showToast("Saved", "success");
      reload();
      setEditing(null);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not save reaction", "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function toggleActive(row: RoomReaction) {
    try {
      await apiRequest(`/admin/room-reactions/${row.id}`, { method: "PATCH", body: { is_active: !row.is_active } });
      showToast(row.is_active ? "Deactivated" : "Activated", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not update reaction", "error");
    }
  }

  async function remove(row: RoomReaction) {
    if (!window.confirm(`Delete "${row.name}"? This cannot be undone.`)) return;
    try {
      await apiRequest(`/admin/room-reactions/${row.id}`, { method: "DELETE" });
      showToast("Deleted", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not delete reaction", "error");
    }
  }

  const columns: Column<RoomReaction>[] = [
    {
      header: "Reaction",
      render: (row) => (
        <div className="flex items-center gap-2">
          {row.asset_url ? (
            <img src={row.asset_url} className="h-10 w-10 rounded-lg object-cover bg-surface-muted" alt="" />
          ) : (
            <div className="h-10 w-10 rounded-lg bg-surface-muted" />
          )}
          <p className="font-medium text-ink">{row.name}</p>
        </div>
      ),
    },
    { header: "Format", render: (row) => <Badge tone="neutral">{row.format.toUpperCase()}</Badge> },
    { header: "Order", render: (row) => row.sort_order },
    { header: "Status", render: (row) => (row.is_active ? <Badge tone="success">Active</Badge> : <Badge tone="neutral">Inactive</Badge>) },
    {
      header: "",
      className: "text-right",
      render: (row) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={() => setEditing(row)}>
            Edit
          </Button>
          <Button size="sm" variant={row.is_active ? "danger" : "secondary"} onClick={() => toggleActive(row)}>
            {row.is_active ? "Deactivate" : "Activate"}
          </Button>
          <Button size="sm" variant="danger" onClick={() => remove(row)}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AppShell title="Room Reactions">
      <p className="mb-4 text-xs text-ink-faint">
        The real, admin-managed catalog behind the room screen's emoji/reaction button — nothing here is
        hardcoded in the app. Format decides which renderer the Android client uses: PNG/WEBP/GIF play via Glide,
        SVGA plays via the same animated player already used for gifts. A seated user's tap animates the reaction
        over their own seat for everyone in the room in realtime; an unseated user's tap posts it into the room's
        chat/reaction feed instead.
      </p>
      <div className="mb-4 flex justify-end">
        <Button onClick={() => setEditing({ format: "png", is_active: true, sort_order: rows.length + 1 })}>+ Add Reaction</Button>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.id}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No room reactions yet"
        pagination={{ page, pageSize: 20, hasNextPage, onPageChange: setPage }}
      />

      <Modal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title={editing?.id ? "Edit Reaction" : "Add Reaction"}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={save} isLoading={isSubmitting}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label="Name" value={editing?.name ?? ""} onChange={(e) => setEditing((p) => ({ ...p, name: e.target.value }))} />
          <Input
            label="Asset URL (PNG / WEBP / GIF / SVGA)"
            value={editing?.asset_url ?? ""}
            onChange={(e) => setEditing((p) => ({ ...p, asset_url: e.target.value }))}
          />
          <Select
            label="Format"
            value={editing?.format ?? "png"}
            onChange={(e) => setEditing((p) => ({ ...p, format: e.target.value as RoomReaction["format"] }))}
          >
            {FORMATS.map((f) => (
              <option key={f} value={f}>
                {f.toUpperCase()}
              </option>
            ))}
          </Select>
          <Input
            label="Sort order"
            type="number"
            value={editing?.sort_order ?? 0}
            onChange={(e) => setEditing((p) => ({ ...p, sort_order: Number(e.target.value) }))}
          />
        </div>
      </Modal>
    </AppShell>
  );
}
